<template>
  <div
    v-if="colors.length + zippers.length + sizes.length"
    class="grid gap-30 mt-30 md:mt-40 lg:mt-50"
  >
    <ProductVariationSelectorButton
      :title="$texts('product.variationSelectorColor')"
      :options="colors"
      show-image
    />

    <ProductVariationSelectorButton
      v-if="zippers.length > 1"
      :title="$texts('product.variationSelectorZippers')"
      :options="zippers"
    />

    <ProductVariationSelectorButton
      v-if="sizes.length > 1"
      :options="sizes"
      :title="$texts('product.variationSelectorSizes')"
    >
      <slot />
    </ProductVariationSelectorButton>
  </div>
</template>

<script lang="ts" setup>
import type {
  ProductVariationFragment,
  TaxonomyTermColorsFragment,
  TaxonomyTermSizesFragment,
  TaxonomyTermZipperVariantsFragment,
} from '#build/graphql-operations'
import { falsy } from '~/helpers/type'

const props = defineProps<{
  activeSku?: string
  variations: ProductVariationFragment[]
}>()

/**
 * A mapped variation used to build the variation toggle.
 */
type MappedVariation = {
  color?: TaxonomyTermColorsFragment
  size?: TaxonomyTermSizesFragment
  zipper?: TaxonomyTermZipperVariantsFragment
  sku: string
  variation: ProductVariationFragment
}

// The possible properties for which variation options are built.
type VariationProperty = 'color' | 'size' | 'zipper'

const mapped = computed<MappedVariation[]>(() =>
  props.variations.map((v) => {
    return {
      color: v.color,
      size: v.size,
      zipper:
        'field_pv_sb_taxref_zippervar' in v
          ? v.field_pv_sb_taxref_zippervar
          : undefined,
      sku: v.sku || '',
      variation: v,
    }
  }),
)

// The currently active variation.
const active = computed(() =>
  mapped.value.find((v) => v.sku === props.activeSku),
)

const activeColorId = computed(() => active.value?.color?.id)
const activeSizeId = computed(() => active.value?.size?.id)
const activeZipperId = computed(() => active.value?.zipper?.id)

const currentSelections = computed(() => {
  return {
    colorId: activeColorId.value,
    sizeId: activeSizeId.value,
    zipperId: activeZipperId.value,
  }
})

/**
 * Score the variation for the best match.
 */
const scoreVariation = (variation: MappedVariation) => {
  let score = 0
  if (variation.color?.id === currentSelections.value.colorId) score++
  if (variation.size?.id === currentSelections.value.sizeId) score++
  if (variation.zipper?.id === currentSelections.value.zipperId) score++
  return score
}

/**
 * Finds the best match for the given property and value.
 */
const findBestMatch = (
  variations: MappedVariation[],
  property: VariationProperty,
  value: string,
): MappedVariation | undefined => {
  return variations
    .filter((variation) => variation[property]?.id === value)
    .sort((a, b) => scoreVariation(b) - scoreVariation(a))[0]
}

// Find unique options for each property
const getMatches = (idType: VariationProperty) => {
  return Array.from(
    new Set(mapped.value.map((v) => v[idType]?.id).filter(falsy)),
  )
    .map((idValue) => findBestMatch(mapped.value, idType, idValue))
    .filter(
      (variation): variation is MappedVariation => variation !== undefined,
    )
}

const colors = computed(() =>
  getMatches('color')
    .filter(
      (v): v is MappedVariation & { color: TaxonomyTermColorsFragment } =>
        !!v.color,
    )
    .map((v) => ({
      sku: v.sku,
      label: v.color.name || '',
      variationLabel: v.variation.label,
      image: v.variation.images?.find((img) => img.__typename === 'MediaImage'),
      active: activeColorId.value === v.color?.id,
    })),
)

const sizes = computed(() =>
  getMatches('size').map((v) => ({
    sku: v.sku,
    label: v.variation.size?.name || '',
    variationLabel: v.variation.label,
    alternativeColor:
      v.color?.id !== activeColorId.value ? v.color?.name : undefined,
    active: activeSizeId.value === v.size?.id,
  })),
)

const zippers = computed(() =>
  getMatches('zipper').map((v) => ({
    sku: v.sku,
    label: v.zipper?.name || '',
    variationLabel: v.variation.label,
    alternativeColor:
      v.color?.id !== activeColorId.value ? v.color?.name : undefined,
    active: activeZipperId.value === v.zipper?.id,
  })),
)

defineOptions({
  name: 'ProductVariationSelector',
})
</script>
